/**
 * "A payment is in flight — do not tear the widget down."
 *
 * Closing the widget runs `render(null, mountPoint)`, which runs every
 * step's cleanup, which destroys the mounted Stripe Element. Do that
 * while `confirmPayment` is running against that Element and the
 * customer watches the widget vanish with a charge possibly in flight.
 *
 * Four things can close the widget and none of them knows anything
 * about payment state: the overlay backdrop, the Escape key, the close
 * button, and the salon's own `SalonLift.close()` — which their page
 * script may call on a timer or a route change. All four funnel through
 * `closeImpl`, so one guard there covers all four.
 *
 * A fifth thing interrupts without closing: the slot-hold countdown,
 * which navigates to the Time step when it reaches zero. See
 * `holdExpiryMayNavigate`.
 *
 * Module-scope rather than widget state on purpose: `closeImpl` lives in
 * the boot layer and must not import the component tree, and the answer
 * has to survive the component being re-rendered underneath it.
 *
 * Keyed by widget session, because a page can hold several widgets — an
 * auto-mounted inline one beside an overlay the salon opens itself. One
 * page-wide flag let a payment in the inline widget refuse
 * `SalonLift.close()` on the overlay, and freeze the overlay's hold
 * countdown, though nothing was being paid there.
 *
 * The lock is raised before a PaymentIntent exists and lowered only on a
 * terminal outcome — never in a `finally`, because a redirect method
 * never returns. See lib/confirm-payment-flow.
 */

const inFlight = new Set<string>();

/** True while a payment in THIS session must not be interrupted. */
export function isPaymentInFlight(sessionId: string): boolean {
  return inFlight.has(sessionId);
}

/**
 * May the slot-hold countdown send the customer back to the Time step?
 *
 * HoldStrip's countdown reaching zero navigates to TIME, which is right
 * on every step but this one. Fire it while `confirmPayment` is running
 * and the Payment step unmounts underneath the call: the charge still
 * completes — the promise and the Element both outlive the component —
 * but the customer is now looking at a time picker, and the money
 * arrives against a cart the UI has already given up on.
 *
 * Skipping the navigation is safe because the server stopped racing
 * this: a cart with a PaymentIntent in flight is not evicted for a
 * further ten minutes (PAYMENT_HOLD_GRACE_MS), so the hold the
 * countdown just wrote off is still there. What the customer is told
 * next comes from the payment's own outcome, which is the only thing
 * that knows whether their money moved.
 */
export function holdExpiryMayNavigate(sessionId: string): boolean {
  return !inFlight.has(sessionId);
}

/**
 * What the countdown's expiry handler does, and what it reports back.
 *
 * Lives here rather than inline in the component because the return
 * value is a contract, and an inline arrow is a contract nothing can
 * test. `false` means "not handled — ask me again", which keeps
 * HoldStrip's latch armed. Anything else latches it, and the strip
 * unmounts at zero.
 *
 * Swallowing a suppressed expiry (returning nothing) is what left a
 * customer whose card was then declined with a lapsed hold, no
 * countdown, and a message about the card that said nothing about the
 * time.
 */
export function onHoldExpiry(sessionId: string, navigate: () => void): boolean | void {
  if (!holdExpiryMayNavigate(sessionId)) return false;
  navigate();
}

export function setPaymentInFlight(sessionId: string, value: boolean): void {
  if (value) inFlight.add(sessionId);
  else inFlight.delete(sessionId);
}
